<template lang="pug">
    .form
        .form__container(v-if="!isLoading")
            .form__title
                slot(name="title")
            form.form__body(
                action="javascript://"
                @submit="sendForm"
            )
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_olympiadName') }}
                    .form__field-body
                        cs-input.form__input(
                            v-model="form.name",
                            v-validate="'required'",
                            :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_olympiadName')",
                            name="olympiad name"
                        )
                        help-text(v-if="errors.has('olympiad name')", error)
                            | {{ errors.first('olympiad name') }}
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_olympiadTitle') }}
                    .form__field-body
                        cs-input.form__input(
                            v-model="form.title",
                            v-validate="'required'",
                            :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_olympiadTitle')",
                            name="olympiad title"
                        )
                        help-text(v-if="errors.has('olympiad title')", error)
                            | {{ errors.first('olympiad title') }}
                .form__field
                    label.form__label
                        | {{ $i18n.tc('olympiads.olympiads_olympiadForm_schools', 1) }}
                    .form__field-body
                        cs-multiselect(
                            :options="schools",
                            v-model="form.schools",
                            :placeholder="schoolsPlaceholder",
                            label="name",
                            trackBy="id",
                            :loading="loadingSchools",
                            @open="getSchools",
                            :hideSelected="true",
                            :showNoResults="false",
                            :closeOnSelect="false",
                        )
                .form__field
                    label.form__label
                        | {{ $i18n.tc('olympiads.olympiads_olympiadForm_grades', 1) }}
                    .form__field-body
                        cs-multiselect(
                            :options="grades",
                            v-model="form.grades",
                            :placeholder="gradesPlaceholder",
                            label="name",
                            trackBy="id",
                            :loading="loadingGrades",
                            @open="getGrades",
                            :hideSelected="true",
                            :showNoResults="false",
                            :disabled="!form.schools.length",
                            :closeOnSelect="false"
                        )
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_students') }}
                    .form__field-body
                        cs-multiselect(
                            :options="students",
                            v-model="form.students",
                            :placeholder="studentsPlaceholder",
                            label="fullName",
                            trackBy="id",
                            :loading="loadingStudents",
                            @open="getStudents",
                            :hideSelected="true",
                            :showNoResults="false",
                            :disabled="!form.grades.length",
                            :closeOnSelect="false"
                        )
                .form__field
                    label.form__label
                    .form__field-body
                        cs-checkbox(v-model="form.availableForOtherStudents")
                            | {{ $i18n.t('olympiads.olympiads_olympiadForm_outsideStudents') }}
                divider
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_registrationDates') }}
                    .form__field-body.form__field-body--inline
                        .date-selector
                            cs-datepicker(
                                :language="user.locale",
                                :value="form.registrationStart",
                                :format="customFormatter",
                                @input="form.registrationStart = dateToMoment($event, 'YYYY-MM-DD')",
                                v-validate="'required|date_format:YYYY-MM-DD'",
                                :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_registrationStartDate')",
                                name="registrationStart",
                                :placeholder="$i18n.t('olympiads.olympiads_olympiadForm_registrationStartDatePlaceholder')"
                            )
                        div.date-divider &mdash;
                        .date-selector
                            cs-datepicker(
                                :language="user.locale",
                                :value="form.registrationEnd",
                                :format="customFormatter",
                                @input="form.registrationEnd = dateToMoment($event, 'YYYY-MM-DD')",
                                v-validate="`required|date_format:YYYY-MM-DD|after:${form.registrationStart},true`",
                                :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_registrationEndDate')",
                                name="registrationEnd",
                                :placeholder="$i18n.t('olympiads.olympiads_olympiadForm_registrationEndDatePlaceholder')"
                            )
                .form__field(:class="{'form__field--small-stack-space': errors.has('registrationEnd') || errors.has('registrationStart')}")
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_registrationTime') }}
                    .form__field-body.form__field-body--inline
                        .date-selector
                            cs-input.form__input(
                                v-model.trim="form.registrationStartTime",
                                v-validate="'required|date_format:HH:mm'",
                                :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_registrationStartTime')",
                                name="registrationStart",
                                :placeholder="$i18n.t('olympiads.olympiads_olympiadForm_registrationStartTimePlaceholder')"
                            )
                        div.date-divider &mdash;
                        .date-selector
                            cs-input.form__input(
                                v-model.trim="form.registrationEndTime",
                                v-validate="'required|date_format:HH:mm' + registrationEndTimeAfter",
                                :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_registrationEndTime')",
                                name="registrationEnd",
                                :placeholder="$i18n.t('olympiads.olympiads_olympiadForm_registrationEndTimePlaceholder')"
                            )
                .form__field(v-if="errors.has('registrationEnd') || errors.has('registrationStart')")
                    label.form__label
                    .form__field-body
                        help-text(error)
                            | {{ errors.first('registrationStart') || errors.first('registrationEnd') }}
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_olympiadDate') }}
                    .form__field-body
                        cs-datepicker.form__input--small(
                            :language="user.locale",
                            :value="form.date",
                            :format="customFormatter",
                            @input="form.date = dateToMoment($event, 'YYYY-MM-DD')",
                            v-validate="`required|date_format:YYYY-MM-DD|after:${form.registrationEnd},true`",
                            :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_olympiadDate')",
                            name="date"
                        )
                        help-text(v-if="errors.has('date')", error) {{ errors.first('date') }}
                divider
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_paymentService') }}
                    .form__field-body
                        cs-multiselect(
                            :multiple="false"
                            v-validate="'required'"
                            :options="paymentServices"
                            @open="getPaymentServices"
                            :loading="loadingPaymentServices"
                            :show-labels="false"
                            :allow-empty="false"
                            placeholder=""
                            v-model="form.paymentService"
                            :searchable="false"
                            label="name"
                            track-by="id"
                            data-vv-name="paymentService"
                            :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_paymentService').toLowerCase()"
                        )
                        help-text(v-if="errors.has('paymentService')", error)
                            | {{ errors.first('paymentService') }}
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_organizationalFee') }}
                    .form__field-body
                        cs-input.form__input.form__input--small(
                            v-model.number="form.price",
                            type="number",
                            v-validate="'required|min_value:0'",
                            :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_organizationalFee')",
                            name="price"
                        )
                        help-text(v-if="errors.has('price')", error)
                            | {{ errors.first('price') }}
                divider
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_userAgreementLink') }}
                    .form__field-body
                        cs-input.form__input(
                            v-model="form.termsOfUseLink",
                            type="text",
                            v-validate="'required|url:require_protocol'",
                            :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_userAgreementLink')",
                            name="termsOfUseLink"
                        )
                        help-text(v-if="errors.has('termsOfUseLink')", error)
                            | {{ errors.first('termsOfUseLink') }}
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_linkToTheRulesOfTheOlympiad') }}
                    .form__field-body
                        cs-input.form__input(
                            v-model="form.rulesLink",
                            type="text",
                            v-validate="'required|url:require_protocol'",
                            :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_linkToTheRulesOfTheOlympiad')",
                            name="rulesLink"
                        )
                        help-text(v-if="errors.has('rulesLink')", error)
                            | {{ errors.first('rulesLink') }}
                divider
                .form__field
                    label.form__label
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_cover') }}
                    .form__field-body
                        cs-image-input(
                            v-model="form.logo",
                            type="olympiad",
                            @showSizeError="showSizeError",
                            :loadText="$i18n.t('profiles.profiles_common_uploadImage')",
                            :removeText="$i18n.t('profiles.profiles_common_removeImage')"
                            :helpText="$i18n.t('common.common_imageInput_sizeHelpText')",
                            :error="showImageWarning ? imageWarningText : ''"
                        )
                template(v-for="(level, index) in form.levels")
                    divider
                    level-form(
                        :level="level",
                        :index="index",
                        :courses="courses",
                        :schools="schools",
                        :grades="grades",
                        :students="students",
                        :loading-courses="loadingCourses"
                        :loading-schools="loadingSchools"
                        :loading-grades="loadingGrades"
                        :loading-student="loadingStudents"
                        :courses-placeholder="studentsPlaceholder"
                        :schools-placeholder="studentsPlaceholder"
                        :grades-placeholder="studentsPlaceholder"
                        :students-placeholder="studentsPlaceholder"
                        :trainer-validator="trainerValidator"
                        :start-time-after="startTimeAfter"
                        @onGradeCloseHandler="onGradeCloseHandler()"
                        @onSchoolCloseHandler="onSchoolCloseHandler()"
                        @getCourses="getCourses"
                        @getSchools="getSchools"
                        @getGrades="getGrades"
                        @getStudents="getStudents"
                        @removeLevel="removeLevel"
                        @editLevel="editLevel"
                        @stopTrainerDate="handleStopTrainerDateEvent($event)"
                    )
                        .level-form__body--error(slot="settings" v-if="isSettingsAvailable(level)")
                            svg-icon.level-form__icon(name="abacus-icon", size="large")
                            span.level-form__text {{ $i18n.t('olympiads.olympiads_olympiadForm_setParameters') }}
                divider
                .form__buttons
                    cs-button.form__button(color="blue" pattern="primary-outline" @click="addLevel", narrow)
                        svg-icon(name="plus" size="large" slot="icon")
                        | {{ $i18n.t('olympiads.olympiads_olympiadForm_addIcon') }}
                divider
                .form__buttons
                    cs-button.form__button(
                        color="blue"
                        pattern="primary-outline"
                        @click.prevent="$router.back()"
                    )
                        | {{ $i18n.t('common.common_button_cancel') }}
                    cs-button.form__button(
                        color="blue"
                        type="submit"
                        :loading="isLoading"
                    )
                        slot(name="button")
        cs-loader(v-else)
</template>

<script>
    import SchoolApi from '../../api/school';
    import GradeApi from '../../api/grade';
    import UserApi from '../../api/user/user';
    import _ from 'lodash';
    import levelTemplate from './config/levelTemplate.json';
    import CourseApi from '../../api/course';
    import OlympiadApi from '../../api/olympiad';
    import moment from 'moment';
    import { mapState } from 'vuex';
    import getBase64FromUrl from '../../utils/getBase64FromUrl';
    import PaymentServicesApi from '../../api/billing/paymentServices';
    import LevelForm from './LevelForm.vue';

    export default {
        components: { LevelForm },
        props: {
            id: [Number, String, undefined],
            isCopy: {
                type: Boolean,
                default: false,
            },
        },
        mixins: [getBase64FromUrl],
        data() {
            return {
                levelTemplate,
                courses: [],
                schools: [],
                grades: [],
                students: [],
                paymentServices: [],
                form: {
                    name: '',
                    title: '',
                    schools: [],
                    grades: [],
                    students: [],
                    availableForOtherStudents: false,
                    registrationStart: null,
                    registrationStartTime: '',
                    registrationEnd: null,
                    registrationEndTime: '',
                    date: null,
                    logo: null,
                    paymentService: null,
                    price: 0,
                    currency: 'RUB',
                    levels: [],
                    termsOfUseLink: 'http://cleverstart.ru/soglashenie/',
                    rulesLink: 'http://cleverstart.ru/',
                },
                loadingPaymentServices: false,
                loadingSchools: false,
                loadingGrades: false,
                loadingStudents: false,
                isLoading: false,
                gradesLoadingAvailable: true,
                studentsLoadingAvailable: true,
                loadingCourses: false,
                imageWarningText: '',
                showImageWarning: false,
            };
        },
        mounted() {
            if (this.id && !this.olympiad) {
                this.isLoading = true;
                OlympiadApi.show(this.id)
                    .then(olympiad => {
                        this.form = _.cloneDeep(olympiad);
                        this.form.levels.forEach(level => {
                            level.isTrainerAvailable = !!level.isTrainerAvailable;
                            if (level.isTrainerAvailable) {
                                level.stopTrainerDate = this.dateToMoment(level.stopTrainerDate, 'YYYY-MM-DD');
                            }
                            level.stages.forEach(stage => {
                                stage.games.forEach(game => {
                                    if (game.slug === 'Puzzle') {
                                        this.assignImageForPuzzleGame(game);
                                        game.parameters.default.isImageChanged = false;
                                    }
                                });
                            });
                        });
                        this.form.registrationStart = this.dateToMoment(olympiad.registrationStart, 'YYYY-MM-DD');
                        this.$set(
                            this.form,
                            'registrationStartTime',
                            this.dateToMoment(olympiad.registrationStart, 'HH:mm')
                        );
                        this.form.registrationEnd = this.dateToMoment(olympiad.registrationEnd, 'YYYY-MM-DD');
                        this.$set(
                            this.form,
                            'registrationEndTime',
                            this.dateToMoment(olympiad.registrationEnd, 'HH:mm')
                        );
                        this.form.date = this.dateToMoment(olympiad.date, 'YYYY-MM-DD');
                        this.isLoading = false;
                    })
                    .catch(err => {
                        err = err.response && err.response.data ? err.response.data : err;
                        if (err && (err.status_code === 403 || err.status_code === 404)) {
                            this.$toasted.error(this.$root.$t(`errors.${err.message}`));
                        }
                    });
            }
        },
        watch: {
            olympiad: {
                handler(newOlympiad) {
                    if (newOlympiad) {
                        this.form = newOlympiad;
                    }
                },
                immediate: true,
            },
            'form.logo': {
                handler(val) {
                    if (val) {
                        this.showImageWarning = false;
                        this.imageWarningText = '';
                    }
                },
            },
            'form.schools.length'(newLength, oldLength) {
                if (newLength !== oldLength) {
                    this.gradesLoadingAvailable = true;
                }
                if (newLength < oldLength) {
                    this.form.grades = this.form.grades.filter(grade =>
                        _.find(this.form.schools, { id: grade.schoolId })
                    );
                    this.form.students = this.form.students.filter(student =>
                        _.find(this.form.schools, { id: student.school.id })
                    );
                    this.grades = this.grades.filter(grade => _.find(this.form.schools, { id: grade.schoolId }));
                    this.students = this.students.filter(student =>
                        _.find(this.form.schools, { id: student.school.id })
                    );
                }
            },
            'form.grades.length'(newLength, oldLength) {
                if (newLength !== oldLength) {
                    this.studentsLoadingAvailable = true;
                }
                if (newLength < oldLength) {
                    this.form.students = this.form.students.filter(student =>
                        _.find(this.form.grades, { id: student.grades[0].id })
                    );
                    this.students = this.students.filter(student =>
                        _.find(this.form.grades, { id: student.grades[0].id })
                    );
                }
            },
        },
        methods: {
            async assignImageForPuzzleGame(game) {
                game.parameters.default.image = await this.getBase64ImageFromUrl(
                    this.$options.filters.apiFile(game.parameters.default.imageSrc)
                );
            },
            dateToMoment(date, format) {
                this.$nextTick(() => {
                    this.$validator.validate('registrationStart');
                    this.$validator.validate('registrationEnd');
                    this.$validator.validate('date');
                });
                return this.customFormatter(date, format);
            },
            onSchoolCloseHandler() {
                (this.gradesLoadingAvailable = true) && this.getGrades();
            },
            onGradeCloseHandler() {
                (this.studentsLoadingAvailable = true) && this.getStudents();
            },
            isSettingsAvailable(level) {
                for (let stage of level.stages) {
                    if (stage.games.length === 0) {
                        return true;
                    }
                }
                return false;
            },
            editLevel(index) {
                this.$store.dispatch('setOlympiad', this.form);
                this.$router.push({ name: 'olympiad-level:create', params: { index: index, olympiadId: this.id } });
            },
            optimizeForm(form) {
                form.students = form.students.map(student => student.id);
                form.grades = form.grades.map(grade => grade.id);
                form.schools = form.schools.map(school => school.id);
                form.levels = form.levels.map(level => {
                    if (level.isRealTime) {
                        level.endTime = null;
                        level.duration = null;
                    } else {
                        level.stages.forEach(stage => {
                            stage.startTime = null;
                            stage.endTime = null;
                        });
                    }
                    level.students = level.students.length ? level.students.map(student => student.id) : [];
                    level.grades = level.grades.length ? level.grades.map(grade => grade.id) : [];
                    level.schools = level.schools.length ? level.schools.map(school => school.id) : [];
                    level.courses = level.courses.length ? level.courses.map(course => course.id) : [];
                    return level;
                });
                form.registrationStart =
                    moment(form.registrationStart).format('YYYY-MM-DD') + ' ' + form.registrationStartTime + ':00';
                form.registrationEnd =
                    moment(form.registrationEnd).format('YYYY-MM-DD') + ' ' + form.registrationEndTime + ':00';
                delete form.registrationStartTime;
                delete form.registrationEndTime;
                form.date = moment(form.date).format('YYYY-MM-DD');
                form.paymentService = form.paymentService.id;
                if (!form.logo) {
                    delete form.logo;
                }
            },
            sendForm() {
                if (_.isEmpty(this.form.levels)) {
                    this.$toasted.info(this.$i18n.t('olympiads.olympiads_olympiadForm_atLeastOne'));
                    return;
                }
                for (let level of this.form.levels) {
                    if (this.isSettingsAvailable(level)) {
                        this.$toasted.info(this.$i18n.t('olympiads.olympiads_olympiadForm_setToContinue'));
                        return;
                    }
                }
                if (
                    this.form.levels.some(
                        level => level.isRealTime && level.stages.some(stage => !stage.startTime || !stage.endTime)
                    )
                ) {
                    this.$toasted.info(this.$i18n.t('olympiads.olympiads_olympiadForm_setTimeForSteps'));
                    return;
                }
                this.$validator.validateAll().then(isValid => {
                    if (isValid) {
                        this.isLoading = true;
                        let form = _.cloneDeep(this.form);
                        this.optimizeForm(form);
                        this.sendRequest(form)
                            .then(() => {
                                this.isLoading = false;
                                this.$router.push({ name: 'olympiads:index' });
                            })
                            .catch(err => {
                                this.isLoading = false;
                                err = err.response && err.response.data ? err.response.data : err;
                                if (err && (err.status_code === 403 || err.status_code === 404)) {
                                    this.$toasted.error(this.$root.$t(`errors.${err.message}`));
                                }
                            });
                    } else {
                        this.$toasted.info(this.$i18n.t('errors.fill_form'));
                    }
                });
            },
            sendRequest(form) {
                return this.id && !this.isCopy ? OlympiadApi.update(this.id, form) : OlympiadApi.create(form);
            },
            removeLevel(index) {
                this.form.levels.forEach(level => {
                    if (level.position > index) {
                        level.position--;
                    }
                });
                this.form.levels.splice(index, 1);
            },
            addLevel() {
                let level = _.cloneDeep(levelTemplate);
                level.position = this.form.levels.length;
                this.form.levels.push(level);
            },
            getSchools() {
                if (this.schools.length) {
                    return;
                }
                this.loadingSchools = true;
                SchoolApi.list({
                    status: 'active',
                    all: true,
                    withGrades: false,
                    withCourses: true,
                })
                    .then(data => {
                        let schools = data.schools;
                        this.schools = schools ? schools : [];
                        this.loadingSchools = false;
                    })
                    .catch(() => {
                        this.loadingSchools = false;
                        this.$toasted.error(this.$i18n.t('errors.getting_schools'));
                    });
            },
            getGrades() {
                let schoolIds = this.grades.length ? _.uniq(this.grades.map(grade => grade.school.id)) : [];
                if (!this.gradesLoadingAvailable || !_.difference(this.getSchoolsIds, schoolIds).length) {
                    return;
                }
                this.loadingGrades = true;
                GradeApi.list({
                    schoolsIds: this.getSchoolsIds,
                    status: 'active',
                    all: true,
                })
                    .then(response => {
                        let grades = response.grades;
                        this.grades = grades ? this.grades.concat(grades) : this.grades;
                        this.gradesLoadingAvailable = false;
                        this.loadingGrades = false;
                    })
                    .catch(() => {
                        this.loadingGrades = false;
                        this.$toasted.error(this.$i18n.t('errors.getting_schools'));
                    });
            },
            getStudents() {
                let gradeIds = this.students.length
                    ? _.uniq(
                          this.students.reduce(
                              (result, student) => result.concat(student.grades.map(grade => grade.id)),
                              []
                          )
                      )
                    : [];
                if (!this.studentsLoadingAvailable || !_.difference(this.getGradesIds, gradeIds).length) {
                    return;
                }
                this.loadingStudents = true;
                UserApi.list({
                    type: 'student',
                    status: 'active',
                    gradesIds: this.getGradesIds,
                    all: true,
                })
                    .then(response => {
                        let students = response.users;
                        this.students = students ? this.students.concat(students) : this.students;
                        this.studentsLoadingAvailable = false;
                        this.loadingStudents = false;
                    })
                    .catch(() => {
                        this.loadingStudents = false;
                        this.$toasted.error(this.$i18n.t('errors.getting_students'));
                    });
            },
            getCourses() {
                if (this.courses.length) {
                    return;
                }
                this.loadingCourses = true;
                CourseApi.list({ all: true, withGames: true })
                    .then(response => {
                        let courses = response.courses;
                        this.courses = courses ? courses : [];
                        this.loadingCourses = false;
                    })
                    .catch(() => {
                        this.loadingCourses = false;
                        this.$toasted.error(this.$i18n.t('errors.getting_students'));
                    });
            },
            getPaymentServices() {
                if (this.paymentServices.length) {
                    return;
                }
                this.loadingPaymentServices = true;
                PaymentServicesApi.list()
                    .then(data => {
                        this.paymentServices = data.data;
                        this.loadingPaymentServices = false;
                    })
                    .catch(() => {
                        this.loadingPaymentServices = false;
                        this.$toasted.error(this.$i18n.t('errors.error_has_occurred'));
                    });
            },
            showSizeError() {
                this.imageWarningText = this.$i18n.t('trainer.trainer_gameFormSetting_imageWeightWarning');
                this.showImageWarning = true;
            },
            customFormatter(date, format = 'D MMM YYYY') {
                return moment(date).format(format);
            },
            handleStopTrainerDateEvent(event) {
                this.form.levels[event.levelId].stopTrainerDate = this.dateToMoment(event['event'], 'YYYY-MM-DD');
            },
        },
        computed: {
            ...mapState({
                olympiad: state => state.olympiad.olympiad,
                user: state => state.auth.user,
            }),
            schoolsPlaceholder() {
                if (this.form.schools.length) {
                    return this.$i18n.t('common.common_placeholder_enterToSearch');
                }
                return this.$i18n.t('olympiads.olympiads_olympiadForm_allSchools');
            },
            studentsPlaceholder() {
                if (this.form.students.length) {
                    return this.$i18n.t('common.common_placeholder_enterToSearch');
                }
                return this.$i18n.t('olympiads.olympiads_olympiadForm_allStudents');
            },
            gradesPlaceholder() {
                if (this.form.grades.length) {
                    return this.$i18n.t('common.common_placeholder_enterToSearch');
                }
                return this.$i18n.t('olympiads.olympiads_olympiadForm_allGrades');
            },
            trainerValidator() {
                return `required|date_format:YYYY-MM-DD|before:${this.form.date}|after:${this.form.registrationStart}`;
            },
            startTimeAfter() {
                let regEnd = this.form.registrationEnd;
                return regEnd && regEnd === this.form.date ? `|after:${this.form.registrationEndTime},true` : '';
            },
            registrationEndTimeAfter() {
                let regEnd = this.form.registrationEnd;
                let regStart = this.form.registrationStart;
                return regEnd && regStart && regEnd === regStart ? `|after:${this.form.registrationStartTime}` : '';
            },
            getSchoolsIds() {
                let schoolIds = this.grades.length ? _.uniq(this.grades.map(grade => grade.school.id)) : [];
                return this.form.schools
                    .map(school => school.id)
                    .concat(
                        this.form.levels.reduce(
                            (result, level) => result.concat(level.schools.map(school => school.id)),
                            []
                        )
                    )
                    .filter(school => !schoolIds.some(schoolId => schoolId === school));
            },
            getGradesIds() {
                let gradeIds = this.students.length
                    ? _.uniq(
                          this.students.reduce(
                              (result, student) => result.concat(student.grades.map(grade => grade.id)),
                              []
                          )
                      )
                    : [];
                return this.form.grades
                    .map(grade => grade.id)
                    .concat(
                        this.form.levels.reduce(
                            (result, level) => result.concat(level.grades.map(grade => grade.id)),
                            []
                        )
                    )
                    .filter(grade => !gradeIds.some(gradeId => gradeId === grade));
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/forms/form.scss';
    @import '@/assets/sass/forms/dates.scss';

    .level-form__body--error {
        text-align: center;
        display: inline-flex;
        align-self: center;
        color: $hsl-color-red-62;
        margin: 20px auto 0;
    }

    .level-form__text {
        font-size: 13px;
        line-height: 24px;
        margin-left: 12px;
    }
</style>
